import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import cx from "classnames";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import Lottie from "react-lottie";
import type { Options as LottieOptions } from "react-lottie";
const LottieDisplay = Lottie as any; // Type assertion needed due to react-lottie types
import animationDataDark from "./lottie/v2.json";
import animationDataLight from "./lottie/v2.json";
import "./App.css";
import Login from "./routes/login";
import { Sidebar } from "./components/sidebar";
import { InstallSuccess } from "./routes/successfullInstall";
import { Report } from "./routes/report";
import { Topbar, TopbarContext } from "./components/topbar";
import styles from "./App.module.scss";
import { mixRegisterSuperProps, mixSetUserProps } from "./assets/mixpanel";
import { toast } from "sonner";
import { IntegrationsInvite } from "./routes/integrations-invite";
import { Reports } from "./routes/reports";
import { Simulators } from "./routes/simulators";
import { SimulatorWrapper } from "./routes/simulators/wrapper";
import { SimulatorWrapper as SimulatorWrapperV2 } from "./v2/features/simulator/context/SimulatorWrapper";
import { ThemeContext } from "./components/Theme";
import { Button } from "@radix-ui/themes";
import { FeatureAccessContext } from "./context/featureAccess";
import { getMemberRole, Role } from "./api/member/Member.model";
import { ChatbotSandbox } from "./routes/chatbot-sandbox";
import { useGetAccess } from "./api/useAccess";
import { SimulatorLiveMode } from "./routes/simulator-live-mode";
import GettingStarted from "./routes/onboarding/getting-started/GettingStarted";
import { automationConfigurationRouteTree } from "@/routes/automation-configuration/automationConfigurationRouteTree";
import { Playground } from "./routes/playground";
import { renderRouteTree } from "@/lib/renderRouteTree";
import { useSettingsRouteTree } from "@/v2/routes/settings/settingsRouteTree";
import { CopilotFeedback } from "./routes/copilot-feedback";
import { Copilot } from "./routes/copilot";
import { KnowledgeGapsPage } from "./routes/knowledge-gaps";
import { KnowledgeGap } from "./routes/knowledge-gap";
import { KnowledgeArticles } from "./routes/knowledge-articles";
import TrackersList from "./routes/trackers";
import IndividualTracker from "./routes/trackers/TrackerDetail";
import { YoavosPlayground } from "./routes/yoavos-playground";
import { Skills } from "./routes/skills";
import { BrowserSkillDetail } from "./routes/skills/BrowserSkillDetail";
import { Enrichments } from "./routes/enrichments";
import CreateEnrichmentPage from "./v2/routes/enrichment/CreateEnrichmentPage";
import EditEnrichmentPage from "./v2/routes/enrichment/EditEnrichmentPage";
import SkillConnectionPage from "./v2/routes/enrichment/SkillConnectionPage";
import SyncPage from "./v2/routes/enrichment/SyncPage";
import { RegressionSets } from "./routes/regression-sets";
import { RegressionSetDetail } from "./routes/regression-sets/[id]";
import { RegressionTests } from "./routes/regression-tests";
import { AuiRegressionTestsList } from "./routes/aui-regression-tests";
import { AuiRegressionTestDetail } from "./routes/aui-regression-tests/Detail";
import { AuiRegressionTestsV2 } from "./v2/features/aui-regression-v2/RegressionTestsV2";
import SimulatorVersions from "./v2/features/simulator-versions";
import { VersionInformationRoute } from "./v2/features/simulator-versions/version-information-route";
import { ReleaseProvider } from "./routes/simulator/context/release";
import { SimulatorActivity } from "./v2/features/simulator-activity";
import RegressionTestsPage from "./v2/features/regression-tests";
import { AiExplore } from "./v2/features/ai-explore";
import { FeatureFlagOverrides } from "./v2/components/feature-flag/FeatureFlagOverrides";
import {
  isQuack,
  useFlag,
} from "./v2/components/feature-flag/FeatureFlagContext";
import CreateTrackerPage from "./routes/trackers/CreateTrackerPage";
import UpdateTrackerPage from "./routes/trackers/UpdateTrackerPage";
import { Toaster } from "@/v2/shared/shadcn-ui/sonner";
import { RegressionTestDetail } from "./routes/regression-tests/[id]";
import { CompareRegressionTests } from "@/routes/regression-tests/CompareRegressionTests";
import CreateEscalationRulePage from "./routes/escalation-rules/CreateEscalationRulePage";
import EditEscalationRulePage from "./routes/escalation-rules/EditEscalationRulePage";
import AppV2 from "./AppV2";
import { SimulatorDistinctVersions } from "./v2/features/simulator/Simulator.versions";
import Home from "./v2/routes/home/Home";
import AdminDashboard from "./v2/routes/admin-dashboard/AdminDashboard";
import BillingPage from "./v2/routes/admin-dashboard/BillingPage";
import TenantStatistics from "./v2/routes/admin-dashboard/TenantStatistics";
import { AgentHome } from "./v2/routes/regression/AgentHome";
import { RegressionSetTests } from "./v2/routes/regression/RegressionSetTests";
import { TestRunDetails } from "./v2/routes/regression/TestRunDetails";
import { SetConfigurationPage } from "./v2/routes/regression/SetConfigurationPage";
import KnowledgeManagementPage from "./v2/routes/knowledge-management/KnowledgeManagementPage";
import GoodResponsesLibraryPage from "./v2/routes/good-responses-library/GoodResponsesLibraryPage";
import { TrainingCenter } from "./routes/training-center/TrainingCenter";
import { ApolloAgent } from "./routes/apollo-agent/ApolloAgent";
import { CreateSkillLab, EditSkillLab } from "./v2/features/skill/lab/SkillLab";
import { CreateSnowflakeSkill } from "./routes/skills/CreateSnowflakeSkill";
import { usePostHogTracking } from "./hooks/usePostHogTracking";
import { posthogTrackEvent, AuthEvents } from "./assets/posthog";
import {
  qualityRoutes,
  oldQualityRoutes,
} from "./v2/routes/quality/qualityRoutes";
import { exploreRoutes } from "./v2/routes/explore/exploreRoutes";
import { approvalRequestsRoutes } from "./v2/routes/approval-requests/approvalRequestsRoutes";
import CustomReportPage from "./v2/routes/custom-report/CustomReportPage";

import Pages from "./v2/features/pages/Pages";
import Journey from "./v2/features/journies/Journey";
import PageDetail from "./v2/features/pages/PageDetail";
import { v4 as uuidv4 } from "uuid";
import { noticeRoutes } from "./v2/routes/notice/noticeRoutes";
import { matchPath } from "react-router-dom";
import { hideTopbarPaths, hideTopbarRoutes } from "@/components/topbar/helper";
import { useRightPanelTopbarStore } from "@/v2/features/simulator/rightPanelTopbar.store";

const onlyAdminsRoutes = new Map([
  ["/", <Home />],
  ["/knowledge-management-editor", <KnowledgeManagementPage />],
  ["/knowledge-management/gaps", <KnowledgeGapsPage />],
  ["/knowledge-management/articles", <KnowledgeArticles />],
  ["/knowledge-management/gaps/:groupId", <KnowledgeGap />],
  ["/automation-center", <Navigate to="/automation-center/training" />],
  ["/automation-center/good-responses-library", <GoodResponsesLibraryPage />],
  [
    "/automation-center/training",
    <ReleaseProvider>
      <TrainingCenter />
    </ReleaseProvider>,
  ],
  ["/automation-center/apollo-agent", <ApolloAgent />],
  [
    "/training-center/:agentId",
    <ReleaseProvider>
      <AgentHome />
    </ReleaseProvider>,
  ],
  [
    "/training-center/:agentId/regression-sets/:setId",
    <ReleaseProvider>
      <RegressionSetTests />
    </ReleaseProvider>,
  ],
  [
    "/training-center/:agentId/regression-sets/:setId/tests/:testId",
    <ReleaseProvider>
      <TestRunDetails />
    </ReleaseProvider>,
  ],
  [
    "/training-center/:agentId/regression-sets/:setId/configure",
    <ReleaseProvider>
      <SetConfigurationPage />
    </ReleaseProvider>,
  ],
  ["/automation-center/skills", <Skills />],
  ["/automation-center/skills/create", <CreateSkillLab />],
  ["/automation-center/skills/create/snowflake/*", <CreateSnowflakeSkill />],
  [
    "/automation-center/skills/:skillId/edit/snowflake",
    <CreateSnowflakeSkill />,
  ],
  ["/automation-center/skills/:skillId/browser", <BrowserSkillDetail />],
  ["/automation-center/skills/:skillId/edit", <EditSkillLab />],
  ["/automation-center/enrichments", <Enrichments />],
  ["/automation-center/enrichments/create", <CreateEnrichmentPage />],
  ["/automation-center/enrichments/:enrichmentId/edit", <EditEnrichmentPage />],
  [
    "/automation-center/enrichments/:enrichmentId/skill-connection",
    <SkillConnectionPage />,
  ],
  ["/automation-center/enrichments/:enrichmentId/sync", <SyncPage />],
  ["/automation-center/regression-sets", <RegressionSets />],
  ["/automation-center/regression-sets/:id", <RegressionSetDetail />],
  [
    "/automation-center/compare/regression-sets/:id/regression-tests/:id1/:id2",
    <CompareRegressionTests />,
  ],
  ["/automation-center/regression-tests", <RegressionTests />],
  ["/automation-center/regression-tests/:id", <RegressionTestDetail />],
  [
    "/automation-center/aui-regression-tests/:manualTestId/:auiTestId",
    <AuiRegressionTestDetail />,
  ],
  ["/automation-center/aui-regression-tests", <AuiRegressionTestsList />],
  ["/automation-center/aui-regression-tests/v2", <AuiRegressionTestsV2 />],
  ["/automation/:simulatorId", <SimulatorDistinctVersions />],
  [
    "/automation/:simulatorId/versions",
    <SimulatorWrapper>
      <SimulatorWrapperV2>
        <SimulatorVersions />
      </SimulatorWrapperV2>
    </SimulatorWrapper>,
  ],
  [
    "/automation/:simulatorId/versions/:versionId",
    <SimulatorWrapper>
      <SimulatorWrapperV2>
        <VersionInformationRoute />
      </SimulatorWrapperV2>
    </SimulatorWrapper>,
  ],
  [
    "/automation/:simulatorId/activity",
    <SimulatorWrapper>
      <SimulatorWrapperV2>
        <SimulatorActivity />
      </SimulatorWrapperV2>
    </SimulatorWrapper>,
  ],
  [
    "/automation/:simulatorId/regression-tests",
    <SimulatorWrapper>
      <SimulatorWrapperV2>
        <RegressionTestsPage />
      </SimulatorWrapperV2>
    </SimulatorWrapper>,
  ],
  [
    "/automation/:simulatorId/:topicId/live-test",
    <SimulatorWrapper>
      <SimulatorLiveMode />
    </SimulatorWrapper>,
  ],
  [
    "/automation",
    <SimulatorWrapper>
      <Simulators />
    </SimulatorWrapper>,
  ],
  ["/ai-explore", <AiExplore />],
  ["/skills/:skillId/browser", <BrowserSkillDetail />],
  ["/skills", <Skills />],
  ["/enrichments", <Enrichments />],
  ["/enrichments/create", <CreateEnrichmentPage />],
  ["/enrichments/:enrichmentId/edit", <EditEnrichmentPage />],
  ["/enrichments/:enrichmentId/skill-connection", <SkillConnectionPage />],
  ["/enrichments/:enrichmentId/sync", <SyncPage />],
  ["/sessions-reports", <Reports />],
  [
    "/automation/:simulatorId/escalation-rules/create",
    <CreateEscalationRulePage />,
  ],
  [
    "/automation/:simulatorId/escalation-rules/edit/:ruleId",
    <EditEscalationRulePage />,
  ],
  ["/getting-started", <GettingStarted />],
  ["/copilot-feedback", <CopilotFeedback />],
  ["/trackers", <TrackersList />],
  ["/trackers/new", <CreateTrackerPage />],
  ["/trackers/:trackerId/edit", <UpdateTrackerPage />],
  ["/trackers/:trackerId", <IndividualTracker />],
  ["/journeys", <Journey />],
  ["/pages", <Pages />],
  ["/pages/:pageId", <PageDetail />],
]);

const onlyUsersRoutes = new Map([
  ["/report/:id", <Report idType="ticketId" />],
  ["/proactive-report/:id", <Report idType="reportId" />],
  ["/copilot/:tenantId/ticket/:ticketId", <Copilot />],
]);

// for it to work, make sure to update 'unprocted' in the sidebar
const openRoutes = new Map([
  ["/installation-successfull", <InstallSuccess />],
  ["/login", <Login />],
  ["/welcome", <IntegrationsInvite source="feature_flag" />],
  ["/upskill", <IntegrationsInvite source="searchparam" />],
  ["/chatbot-sandbox", <ChatbotSandbox />],
  ["/playground", <Playground />],
  ["/yoavos-playground", <YoavosPlayground />],
  ["/demo", <Playground />],
  ["/custom-report/:exploreViewId", <CustomReportPage />],
]);

const renderUnauthorized = (logout: () => void) => (
  <div id="main" className={styles.container}>
    <div className={styles.secondContainer}>
      <div
        style={{
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          paddingTop: "300px",
          gap: "16px",
        }}
      >
        Sorry, you can try a different account{" "}
        <Button
          variant={"solid"}
          color={"gray"}
          highContrast
          onClick={() => {
            posthogTrackEvent({
              event: AuthEvents.LOGOUT,
              properties: {
                reason: "unauthorized_workspace",
                timestamp: new Date().toISOString(),
              },
            });
            logout();
          }}
        >
          logout
        </Button>
      </div>
    </div>
  </div>
);

function App() {
  const [path, setPath] = useState("");
  const { user, logout, isLoading } = useAuth0();
  const location = useLocation();

  // Initialize PostHog tracking
  usePostHogTracking();
  const [isAnimationLoading, setAnimationLoading] = useState(true);
  const [isWorkspaceChange, setWorkspaceChange] = useState(false);
  const { selectedWorkspace } = useContext(FeatureAccessContext);
  const { isDarkMode } = useContext(ThemeContext);
  const isQuackUser = useMemo(() => isQuack(user), [user]);
  const newAppLayoutEnabled = useFlag("newAppLayout", false);
  const dynamicSettingsRouteTree = useSettingsRouteTree();
  const noticesEnabled = useFlag("notices", false);
  const hasRightPanelTopbar = useRightPanelTopbarStore(
    (state) => state.hasRightPanelTopbar
  );

  const subdomain = useMemo(() => {
    return window.location.hostname.split(".")[0];
  }, []);

  // Add check for automation-related paths
  const isFromAutomation = useMemo(() => {
    return (
      location.pathname.includes("automation") ||
      location.pathname.includes("simulator")
    );
  }, [location.pathname]);

  useEffect(() => {
    setTimeout(() => {
      setAnimationLoading(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (user) {
      let currentSessionId = sessionStorage.getItem("sessionId");

      if (!currentSessionId) {
        currentSessionId = uuidv4();
        sessionStorage.setItem("sessionId", currentSessionId || "");
      }

      Sentry.setTag("sessionId", currentSessionId);

      mixSetUserProps(true, {
        name: user?.name,
        email: user?.email,
        id: user?.sub,
        tenant: selectedWorkspace,
        webapp_user: true,
      });
      mixRegisterSuperProps({
        webapp_event: true,
      });
      Sentry.setUser({
        id: user?.email,
        email: user?.email,
      });
      Sentry.setTag("tenant", selectedWorkspace);
    } else {
      sessionStorage.removeItem("sessionId");
      Sentry.setTag("sessionId", undefined);
    }
  }, [user, selectedWorkspace]);

  const { data: membersData, isLoading: membersLoading } = useGetAccess(!!user);

  const isAdmin = membersData && getMemberRole(membersData) !== Role.AGENT;

  useEffect(() => {
    const currentPath = location.pathname;
    const isAdminRoute = Array.from(onlyAdminsRoutes.keys()).some(
      (path) =>
        path === currentPath ||
        (path.includes(":") && currentPath.startsWith(path.split(":")[0]))
    );

    if (
      !membersLoading &&
      !isLoading &&
      membersData !== undefined &&
      isAdminRoute &&
      !isAdmin
    ) {
      toast.error("Not Authorized user, Contact your manager");
    }
  }, [isAdmin, membersData, location, membersLoading, isLoading]);

  const renderRoute = useCallback(
    (path: string, element: React.ReactNode) => {
      if (openRoutes.has(path)) {
        return <Route key={path} path={path} element={element} />;
      }
      if (onlyAdminsRoutes.has(path)) {
        return (
          <Route
            key={path}
            path={path}
            element={isAdmin ? element : renderUnauthorized(logout)}
          />
        );
      }
      if (onlyUsersRoutes.has(path)) {
        return <Route key={path} path={path} element={element} />;
      }
      return null;
    },
    [isAdmin, logout]
  );

  useEffect(() => {
    setWorkspaceChange(true);
    setTimeout(() => {
      setWorkspaceChange(false);
    }, 3);
  }, [selectedWorkspace]);

  const defaultRouteUrl = "/";

  if (newAppLayoutEnabled) {
    return <AppV2 />;
  }

  const hideTopbar = useMemo(() => {
    return (
      hideTopbarPaths.some((path) => matchPath(path, location.pathname)) ||
      hideTopbarRoutes.some((route) => location.pathname.includes(route))
    );
  }, [location]);

  return (
    <div
      id="main"
      className={cx(
        {
          [styles.dark_mode]: isDarkMode,
          [styles.light_mode]: !isDarkMode,
        },
        styles.container
      )}
    >
      {isQuackUser && <FeatureFlagOverrides />}
      {!isLoading && !membersLoading && !isAnimationLoading ? (
        <>
          <Sidebar />
          {isWorkspaceChange ? (
            <></>
          ) : (
            <div
              className={cx(
                {
                  [styles.has_Topbar]: hasRightPanelTopbar,
                },
                styles.secondContainer
              )}
            >
              {!hideTopbar && <Topbar/>}
              <TopbarContext.Provider value={{ path: path, setPath: setPath }}>
                <Routes>
                  {[
                    ...Array.from(onlyAdminsRoutes),
                    ...Array.from(onlyUsersRoutes),
                    ...Array.from(openRoutes),
                  ].map(([path, element]) => renderRoute(path, element))}

                  {isAdmin && renderRouteTree(dynamicSettingsRouteTree)}

                  {renderRouteTree(automationConfigurationRouteTree)}

                  {isAdmin && qualityRoutes}

                  {isAdmin && exploreRoutes}

                  {isAdmin && approvalRequestsRoutes}

                  {isAdmin && noticesEnabled && noticeRoutes}

                  {isQuackUser && (
                    <>
                      <Route
                        path="/admin-dashboard"
                        element={<AdminDashboard />}
                      />
                      <Route
                        path="/admin-dashboard/billing"
                        element={<BillingPage />}
                      />
                      <Route
                        path="/admin-dashboard/tenants/statistics"
                        element={<TenantStatistics />}
                      />
                    </>
                  )}

                  <Route path="*" element={<Navigate to={defaultRouteUrl} />} />
                  {/* <Route path="*" element={<div>404</div>} /> */}
                </Routes>
              </TopbarContext.Provider>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LottieDisplay
            options={
              {
                loop: true,
                autoplay: true,
                animationData: isDarkMode
                  ? animationDataDark
                  : animationDataLight,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              } as LottieOptions
            }
            height={120}
            width={120}
            isClickToPauseDisabled={true}
          />
        </div>
      )}

      <Toaster position="top-center" />
    </div>
  );
}

export default App;
